// Head size guide overlay for the crop frame.
//
// Shows visual guidelines indicating where the head should be positioned for
// the selected passport photo size.
//

import { PHOTO_SIZES, type PhotoSize } from "../utils/dimensions";

// The guide shows:
// - Top margin zone (where top of head should be)
// - Valid head height zone (green area)
// - Chin line range
// - Valid head-edge zones when the profile specifies a head width
//

interface HeadGuideOverlayProps {
  containerWidth: number;
  containerHeight: number;
  photoSize: PhotoSize;
}

// Head guide overlay component.
//
export function HeadGuideOverlay({
  containerWidth,
  containerHeight,
  photoSize,
}: HeadGuideOverlayProps) {
  const headRequirements = PHOTO_SIZES[photoSize].head;

  // Calculate positions based on head size requirements.
  //
  const headTopY = containerHeight * (headRequirements.guideTopPercent / 100);
  const minHeadHeight = containerHeight * (headRequirements.minPercent / 100);
  const maxHeadHeight = containerHeight * (headRequirements.maxPercent / 100);
  const widthRequirements = headRequirements.width;
  const minHeadWidth = widthRequirements
    ? containerWidth * (widthRequirements.minPercent / 100)
    : 0;
  const maxHeadWidth = widthRequirements
    ? containerWidth * (widthRequirements.maxPercent / 100)
    : 0;
  const edgeBandWidth = (maxHeadWidth - minHeadWidth) / 2;

  // Chin positions.
  //
  const minChinY = headTopY + minHeadHeight; // Minimum chin position (smallest head)
  const maxChinY = headTopY + maxHeadHeight; // Maximum chin position (largest head)

  return (
    <div className="pointer-events-none absolute inset-0">
      {/* Top margin indicator */}
      <div
        className="absolute right-0 left-0 border-b-2 border-dashed border-blue-400/60"
        style={{ top: headTopY }}
      >
        <span className="absolute -top-5 left-1 text-xs font-medium text-blue-600 dark:text-blue-400">
          Top of head
        </span>
      </div>

      {/* Valid chin zone (shaded yellow area between min and max chin) */}
      <div
        className="absolute right-0 left-0 bg-amber-400/20"
        style={{
          top: minChinY,
          height: maxChinY - minChinY,
        }}
      >
        <span className="absolute top-1/2 left-2 -translate-y-1/2 rounded bg-white/80 px-1 text-xs font-medium whitespace-nowrap text-amber-700 dark:bg-gray-900/80 dark:text-amber-400">
          Chin inside this band
        </span>
      </div>

      {/* Min chin line (for smallest acceptable head) */}
      <div
        className="absolute right-0 left-0 border-b-2 border-dashed border-amber-500/60"
        style={{ top: minChinY }}
      >
        <span className="absolute right-1 -bottom-5 text-xs font-medium text-amber-600 dark:text-amber-400">
          Min chin
        </span>
      </div>

      {/* Max chin line (for largest acceptable head) */}
      <div
        className="absolute right-0 left-0 border-b-2 border-dashed border-amber-500/60"
        style={{ top: maxChinY }}
      >
        <span className="absolute right-1 -bottom-5 text-xs font-medium text-amber-600 dark:text-amber-400">
          Max chin
        </span>
      </div>

      {/* Valid head-edge zones for profiles with a width requirement */}
      {widthRequirements && (
        <>
          <div
            className="absolute border-x-2 border-dashed border-green-500/70 bg-green-500/10"
            style={{
              left: (containerWidth - maxHeadWidth) / 2,
              top: headTopY,
              width: edgeBandWidth,
              height: maxHeadHeight,
            }}
          />
          <div
            className="absolute border-x-2 border-dashed border-green-500/70 bg-green-500/10"
            style={{
              left: (containerWidth + minHeadWidth) / 2,
              top: headTopY,
              width: edgeBandWidth,
              height: maxHeadHeight,
            }}
          />
          <span
            className="absolute z-10 -translate-x-1/2 rounded bg-white/80 px-1 text-xs whitespace-nowrap text-green-700 dark:bg-gray-900/80 dark:text-green-400"
            style={{ left: "50%", top: headTopY + 4 }}
          >
            Head width {widthRequirements.label}
          </span>
        </>
      )}

      {/* Center vertical guide */}
      <div className="absolute top-0 bottom-0 left-1/2 w-0 -translate-x-1/2 border-l-2 border-dashed border-red-500/90" />

      {/* Head height bracket */}
      <div
        className="absolute right-2 border-r-2 border-blue-500/80"
        style={{
          top: headTopY,
          height: maxHeadHeight,
        }}
      >
        <div className="absolute top-0 right-0 w-2 border-t-2 border-blue-500/80" />
        <div
          className="absolute right-0 w-2 border-t-2 border-amber-500/80"
          style={{ top: minHeadHeight }}
        />
        <div className="absolute right-0 bottom-0 w-2 border-b-2 border-amber-500/80" />
        <span className="absolute top-1/2 -right-1 -translate-y-1/2 rounded bg-white/80 px-1 text-xs whitespace-nowrap text-gray-700 dark:bg-gray-900/80 dark:text-gray-300">
          Head height {headRequirements.label}
        </span>
      </div>
    </div>
  );
}
